import { Tooltip } from '@heroui/react';
import React from 'react';
import { FiCopy, FiCheck } from 'react-icons/fi';

import { useCopyToClipboard } from '../../../../../hooks/useCopyToClipboard';

interface CopyableIdChipProps {
  /** Short prefix shown in front of the value, e.g. "Ref". */
  label: string;
  /** How the identifier is named to a person, e.g. "reference ID". */
  name: string;
  value?: string | null;
  /**
   * Show only this many leading characters. A UUID is never read, only pasted,
   * so the header stays on one line and the whole value goes to the clipboard.
   */
  shortenTo?: number;
  testId: string;
}

/**
 * CS copies these into tickets and searches constantly, so the whole chip is
 * the control rather than a value you have to select by hand.
 */
export const CopyableIdChip: React.FC<CopyableIdChipProps> = ({
  label,
  name,
  value,
  shortenTo,
  testId,
}) => {
  const { copy, justCopied } = useCopyToClipboard(name);

  if (!value) {
    return (
      <span className="rounded-md bg-default-100 px-2 py-1 text-xs text-default-500">
        No {name}
      </span>
    );
  }

  const isShortened = shortenTo !== undefined && value.length > shortenTo;
  const shownValue = isShortened ? `${value.slice(0, shortenTo)}\u2026` : value;
  // A shortened value is unreadable on the chip, so the tooltip carries it.
  const copyHint = isShortened ? `Copy ${name}: ${value}` : `Copy ${name}`;

  return (
    <Tooltip content={justCopied ? 'Copied' : copyHint} placement="bottom">
      <button
        type="button"
        onClick={() => copy(value)}
        data-testid={testId}
        aria-label={`Copy ${name} ${value}`}
        className="inline-flex items-center gap-1.5 rounded-md bg-default-100 px-2 py-1 text-default-700 transition-colors hover:bg-default-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
      >
        <span className="text-xs text-default-500">{label}</span>
        <span className="font-mono text-xs">{shownValue}</span>
        {justCopied ? (
          <FiCheck size={12} className="text-success" />
        ) : (
          <FiCopy size={12} className="text-default-500" />
        )}
      </button>
    </Tooltip>
  );
};
